import type { FiveSBoardMachine, FiveSBoardStatus } from '@/types/api'

/**
 * The 5S board's logic, kept out of the components so it can be tested.
 *
 * The server decides each machine's state (see `FiveSBoardStatus`); what lives
 * here is everything the screen does with it — the order the machines are read
 * in, the quick filters and their counts, the month in the URL, and a score's
 * trend. The colour a score is drawn in is the company's 5S target, and lives
 * in `lib/fiveSTarget.ts` with every other screen's.
 */

// ---------------------------------------------------------------------------
// Scores
// ---------------------------------------------------------------------------

/**
 * The movement between the last two months that have a score, or null.
 *
 * Across a gap on purpose: a machine walked in September and November moved
 * from one to the other, and "no change since October" would be a claim about
 * a month nobody looked at.
 */
export function trendDelta(trend: (number | null)[]): number | null {
  const scored = trend.filter((value): value is number => value !== null)

  if (scored.length < 2) return null

  return Math.round((scored[scored.length - 1] - scored[scored.length - 2]) * 10) / 10
}

/**
 * Points for a sparkline of a trend, with breaks where a month had no walk.
 *
 * Returns one array per unbroken run, so a gap is drawn as a gap rather than
 * as a line through a month nobody walked. The y axis is always 0–100, never
 * fitted to the data — a sparkline rescaled to its own range makes 61 % and
 * 64 % look like a crisis.
 */
export function sparkline(
  trend: (number | null)[],
  width: number,
  height: number,
  pad = 2,
): { x: number; y: number; value: number }[][] {
  const runs: { x: number; y: number; value: number }[][] = []
  const step = trend.length > 1 ? (width - pad * 2) / (trend.length - 1) : 0
  let run: { x: number; y: number; value: number }[] = []

  trend.forEach((value, index) => {
    if (value === null) {
      if (run.length) runs.push(run)
      run = []
      return
    }

    const clamped = Math.max(0, Math.min(100, value))

    run.push({
      x: Math.round((pad + index * step) * 10) / 10,
      y: Math.round((pad + (1 - clamped / 100) * (height - pad * 2)) * 10) / 10,
      value,
    })
  })

  if (run.length) runs.push(run)

  return runs
}

// ---------------------------------------------------------------------------
// Order and grouping
// ---------------------------------------------------------------------------

/**
 * The order a machine is read in: what can be done now first.
 *
 * A started walk leads — somebody is halfway round that machine, and finishing
 * it is the cheapest progress there is. Then the ones not walked, then the
 * walked ones. A machine with no checklist goes last: nobody on the floor can
 * walk it, and it is the administrator's to fix, not the evaluator's.
 */
const STATUS_ORDER: Record<FiveSBoardStatus, number> = {
  draft: 0,
  due: 1,
  walked: 2,
  noTemplate: 3,
}

/** Stable: within one state, the server's order (the plant's own sort order). */
export function byUrgency<T extends Pick<FiveSBoardMachine, 'status'>>(machines: T[]): T[] {
  return machines
    .map((machine, index) => ({ machine, index }))
    .sort((a, b) => STATUS_ORDER[a.machine.status] - STATUS_ORDER[b.machine.status] || a.index - b.index)
    .map(({ machine }) => machine)
}

export interface PlantGroup {
  id: string
  name: string
  machines: FiveSBoardMachine[]
  /** Machines that can be walked — the coverage bar's denominator. */
  total: number
  walked: number
}

/**
 * The board as it is drawn: one group per plant, unwalked machines first.
 *
 * Plants keep the server's order, which is their own sort order. A plant
 * whose every machine was filtered away is left out rather than drawn as an
 * empty heading.
 *
 * The counts are the plant's whole month whatever the filter shows: "Za
 * obilazak" narrowing Livprom to its one unwalked machine must still say
 * "4 / 5 obiđeno", not "0 / 1".
 */
export function groupByPlant(
  machines: FiveSBoardMachine[],
  visible: (machine: FiveSBoardMachine) => boolean = () => true,
): PlantGroup[] {
  const groups = new Map<string, PlantGroup>()

  for (const machine of machines) {
    const id = machine.plant?.id ?? ''
    const group = groups.get(id) ?? {
      id,
      name: machine.plant?.name ?? '',
      machines: [],
      total: 0,
      walked: 0,
    }

    if (visible(machine)) group.machines.push(machine)
    if (machine.status !== 'noTemplate') group.total += 1
    if (machine.status === 'walked') group.walked += 1

    groups.set(id, group)
  }

  return [...groups.values()]
    .filter((group) => group.machines.length > 0)
    .map((group) => ({ ...group, machines: byUrgency(group.machines) }))
}

// ---------------------------------------------------------------------------
// Quick filters
// ---------------------------------------------------------------------------

/**
 * The chips above the board, as they appear in the URL.
 *
 * ASCII on purpose: `?prikaz=obidjene` survives being pasted into a chat,
 * where `obiđene` arrives percent-encoded and unreadable.
 */
export const BOARD_FILTERS = ['sve', 'za-obilazak', 'obidjene', 'moje'] as const

export type BoardFilter = (typeof BOARD_FILTERS)[number]

/**
 * The chip the board opens on.
 *
 * Somebody who cannot evaluate opens 5S to see how *their* machines did —
 * the scores are posted for them, not a list of jobs — so a worker with
 * machines of their own starts on those. Everybody else starts on all of
 * them, since the evaluator's question is the plant's.
 */
export function defaultBoardFilter(canEvaluate: boolean, mine: number): BoardFilter {
  return !canEvaluate && mine > 0 ? 'moje' : 'sve'
}

/**
 * The chip a `?prikaz=` names, falling back to the default for anything it
 * does not know — and away from "moje" for somebody with no machines, so an
 * old link never opens on an empty board.
 */
export function resolveBoardFilter(value: string | null, fallback: BoardFilter, mine: number): BoardFilter {
  const chosen = (BOARD_FILTERS as readonly string[]).includes(value ?? '') ? (value as BoardFilter) : fallback

  return chosen === 'moje' && mine === 0 ? 'sve' : chosen
}

const MATCHES: Record<BoardFilter, (machine: FiveSBoardMachine) => boolean> = {
  sve: () => true,
  'za-obilazak': (machine) => machine.status === 'due' || machine.status === 'draft',
  obidjene: (machine) => machine.status === 'walked',
  moje: (machine) => machine.isMine,
}

export function applyBoardFilter(machines: FiveSBoardMachine[], filter: BoardFilter): FiveSBoardMachine[] {
  return machines.filter(MATCHES[filter])
}

/** The test behind a chip, for `groupByPlant`'s `visible`. */
export function boardFilterTest(filter: BoardFilter): (machine: FiveSBoardMachine) => boolean {
  return MATCHES[filter]
}

/** How many machines each chip would show, so the count is on the chip. */
export function boardFilterCounts(machines: FiveSBoardMachine[]): Record<BoardFilter, number> {
  return Object.fromEntries(
    BOARD_FILTERS.map((filter) => [filter, machines.filter(MATCHES[filter]).length]),
  ) as Record<BoardFilter, number>
}

// ---------------------------------------------------------------------------
// The month, in the URL
// ---------------------------------------------------------------------------

/** `2026-11` — the board's `?mjesec=`. */
export function monthParam(month: { year: number; month: number }): string {
  return `${month.year}-${String(month.month).padStart(2, '0')}`
}

/**
 * The month a `?mjesec=` names, or undefined for "this month".
 *
 * Undefined rather than a guess for anything malformed, so a mangled link
 * opens on the current month — the server answers a month that has not
 * started the same way.
 */
export function parseMonthParam(value: string | null): { year: number; month: number } | undefined {
  const match = /^(\d{4})-(\d{2})$/.exec(value ?? '')

  if (!match) return undefined

  const year = Number(match[1])
  const month = Number(match[2])

  return month >= 1 && month <= 12 ? { year, month } : undefined
}

// ---------------------------------------------------------------------------
// The walk form's picker
// ---------------------------------------------------------------------------

/**
 * Letters as they would be typed with a glove on a phone keyboard.
 *
 * "krojenje" finds "Krojenje", "montaza" finds "Montaža": nobody standing at a
 * machine switches keyboard layout to type a č.
 */
export function foldText(text: string): string {
  return text
    .toLowerCase()
    .replace(/đ/g, 'dj')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
}

/** Machines matching a search over the name, the code and the plant. */
export function searchMachines<T extends Pick<FiveSBoardMachine, 'name' | 'code' | 'plant'>>(
  machines: T[],
  query: string,
): T[] {
  const needle = foldText(query.trim())

  if (!needle) return machines

  return machines.filter((machine) =>
    foldText(`${machine.name} ${machine.code} ${machine.plant?.name ?? ''}`).includes(needle),
  )
}
